// Team.jsx - Staff accounts. Add, edit, deactivate, and change your own password.
//
// Roles are coarse on purpose. Three of them cover everything this business does,
// and a permission matrix nobody understands is worse than a role somebody has to
// think about for two seconds.

import { useState } from "react";

import { useAuth } from "./AdminApp.jsx";
import { useAsync, useForm } from "../lib/hooks.js";
import { adminApi, authApi } from "../lib/api.js";
import { ago, initials, phone as formatPhone } from "../lib/format.js";
import { Button, Panel, Input, Select, Modal, Alert, Loading, Tag, Empty, useToast } from "../ui/kit.jsx";
import { Plus, Users, Trash, Edit } from "../ui/icons.jsx";

const ROLE_NOTES = {
  owner: "Everything, including creating other owners and deleting orders.",
  manager: "Everything except creating owners. Can see and change settings and payouts.",
  agent: "Orders, appraisals, offers and messages. Cannot see payout details or change settings.",
};

////////// PERSON DIALOG //////////

function PersonDialog({ open, onClose, person, roles, onSaved }) {
  const toast = useToast();
  const editing = Boolean(person);

  const form = useForm({
    name: person?.name || "",
    email: person?.email || "",
    phone: person?.phone || "",
    role: person?.role || "agent",
    password: "",
  });

  const submit = form.submit(async (values) => {
    if (editing) {
      const patch = { name: values.name, phone: values.phone, role: values.role };
      if (values.password) patch.password = values.password;
      await adminApi.updateUser(person._id, patch);
    } else {
      await adminApi.createUser(values);
    }
    toast.success(editing ? "Saved" : `${values.name} can sign in now`);
    onSaved();
    onClose();
    return true;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${person.name}` : "Add someone"}
      description={editing ? undefined : "They can sign in straight away with the password you set here."}
      footer={<><Button variant="quiet" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={form.busy}>{editing ? "Save" : "Create account"}</Button></>}
    >
      <div className="space-y-4">
        <Input label="Name" required {...form.field("name")} />
        <Input label="Email" type="email" required disabled={editing} {...form.field("email")} />
        <Input
          label="Mobile number"
          type="tel"
          hint="Needed if they want text alerts."
          {...form.field("phone")}
        />
        <Select
          label="Role"
          options={roles.map((role) => ({ value: role.key, label: role.label }))}
          hint={ROLE_NOTES[form.values.role]}
          {...form.field("role")}
        />
        <Input
          label={editing ? "New password" : "Password"}
          type="password"
          autoComplete="new-password"
          required={!editing}
          hint={editing ? "Leave blank to keep the current one. Changing it signs them out everywhere." : "At least 10 characters."}
          {...form.field("password")}
        />
        {form.message && <Alert tone="error">{form.message.text}</Alert>}
      </div>
    </Modal>
  );
}

////////// MY PASSWORD //////////

function PasswordDialog({ open, onClose }) {
  const toast = useToast();
  const form = useForm({ current: "", next: "" });

  const submit = form.submit(async (values) => {
    await authApi.changePassword(values);
    toast.success("Password changed");
    onClose();
    return true;
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change your password"
      description="This signs you out of every other browser."
      footer={<><Button variant="quiet" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={form.busy}>Change it</Button></>}
    >
      <div className="space-y-4">
        <Input label="Current password" type="password" autoComplete="current-password" required {...form.field("current")} />
        <Input label="New password" type="password" autoComplete="new-password" required hint="At least 10 characters." {...form.field("next")} />
        {form.message && <Alert tone="error">{form.message.text}</Alert>}
      </div>
    </Modal>
  );
}

////////// PAGE //////////

export default function Team() {
  const toast = useToast();
  const { user, can } = useAuth();
  const { data, loading, error, reload } = useAsync(() => adminApi.users(), []);

  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [removing, setRemoving] = useState(null);

  if (loading) return <Loading label="Loading the team" />;
  if (error) return <div className="p-8"><Alert tone="error">{error.message}</Alert></div>;

  const toggleActive = async (person) => {
    try {
      await adminApi.updateUser(person._id, { active: !person.active });
      toast.success(person.active ? `${person.name} deactivated` : `${person.name} reactivated`);
      reload();
    } catch (problem) {
      toast.error(problem.message);
    }
  };

  const remove = async () => {
    try {
      await adminApi.deleteUser(removing._id);
      toast.success(`Removed ${removing.name}`);
      setRemoving(null);
      reload();
    } catch (problem) {
      toast.error(problem.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1000px]">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="display display-lg text-ink">Team</h1>
          <p className="hint mt-1">Everyone who can sign in to this dashboard.</p>
        </div>
        <div className="flex gap-2 w-full xs:w-auto">
          <Button variant="quiet" onClick={() => setChangingPassword(true)} className="flex-1 xs:flex-none">My password</Button>
          {can("users.read") && <Button onClick={() => setAdding(true)} className="flex-1 xs:flex-none"><Plus size={16} /> Add someone</Button>}
        </div>
      </header>

      <Panel>
        {data?.users?.length ? (
          <ul className="divide-y divide-rule-soft">
            {data.users.map((person) => (
              <li key={person._id} className="px-4 py-3.5 flex flex-wrap items-center gap-x-4 gap-y-2.5">
                <span className="w-9 h-9 rounded-full bg-bronze-wash text-bronze-dark text-[12px] font-bold flex items-center justify-center shrink-0">
                  {initials(person.name)}
                </span>

                <div className="min-w-[10rem] flex-1">
                  <p className="text-[14.5px] font-medium text-ink">
                    {person.name}
                    {person._id === user._id && <span className="text-faint font-normal"> (you)</span>}
                  </p>
                  <p className="hint num">{person.email}{person.phone ? ` · ${formatPhone(person.phone)}` : ""}</p>
                </div>

                <Tag tone={person.role === "owner" ? "bronze" : "neutral"}>{person.role}</Tag>
                {!person.active && <Tag tone="critical">Deactivated</Tag>}

                <span className="text-[12px] text-faint whitespace-nowrap hidden sm:inline">
                  {person.lastLoginAt ? `Seen ${ago(person.lastLoginAt)}` : "Never signed in"}
                </span>

                <div className="flex gap-1 ml-auto">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(person)} aria-label={`Edit ${person.name}`}>
                    <Edit size={15} />
                  </Button>
                  {person._id !== user._id && can("users.read") && (
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(person)}>
                      {person.active ? "Deactivate" : "Reactivate"}
                    </Button>
                  )}
                  {person._id !== user._id && can("*") && (
                    <Button size="sm" variant="ghost" onClick={() => setRemoving(person)} aria-label={`Remove ${person.name}`}>
                      <Trash size={15} />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <Empty title="Nobody here yet" icon={Users} />
        )}
      </Panel>

      <Panel quiet className="p-5 mt-5">
        <p className="eyebrow mb-3">What the roles mean</p>
        <dl className="space-y-2.5">
          {(data?.roles || []).map((role) => (
            <div key={role.key} className="grid sm:grid-cols-[110px_minmax(0,1fr)] gap-1 sm:gap-4">
              <dt className="text-[13.5px] font-semibold text-ink">{role.label}</dt>
              <dd className="hint leading-relaxed">{ROLE_NOTES[role.key]}</dd>
            </div>
          ))}
        </dl>
      </Panel>

      <PersonDialog
        open={adding || Boolean(editing)}
        person={editing}
        roles={data?.roles || []}
        onClose={() => { setAdding(false); setEditing(null); }}
        onSaved={reload}
      />

      <PasswordDialog open={changingPassword} onClose={() => setChangingPassword(false)} />

      <Modal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.name}?`}
        description="Their account is deleted. Everything they did stays in the log."
        footer={<><Button variant="quiet" onClick={() => setRemoving(null)}>Cancel</Button><Button variant="danger" onClick={remove}>Remove</Button></>}
      >
        <Alert tone="warning">
          Deactivating is usually better. It stops them signing in but keeps the account if they come back.
        </Alert>
      </Modal>
    </div>
  );
}
